import { useState, useMemo } from 'react';
import { Card, Table, Spin, Typography, message } from 'antd';
import { TemplatePreview } from './TemplatePreview';
import { TemplateEditorForm } from './TemplateEditorForm';
import { getTemplateColumns } from './TemplateTableColumns';
import { useEmailTemplates } from '../../hooks/useEmailTemplates';
import '../../index.css'



const { Title, Text } = Typography;

export const EmailTemplateConfig = () => {
  const [selectedType, setSelectedType] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const {
    templates,
    isLoading,
    isSaving,
    getTemplate,
    updateTemplateLocally,
    saveTemplate,
    resetTemplate,
  } = useEmailTemplates();

  const currentTemplate = useMemo(
    () => (selectedType ? getTemplate(selectedType) : null),
    [selectedType, getTemplate]
  );

  const columns = getTemplateColumns(setSelectedType);

  const handleSubjectChange = (value) => {
    if (!selectedType) return;
    updateTemplateLocally(selectedType, { subject: value });
  };

  const handleContentChange = (content) => {
    if (!selectedType) return;
    updateTemplateLocally(selectedType, { content });
  };

  const handleEmailChange = (index, value) => {
    if (!selectedType) return;
    const newEmails = [...(currentTemplate.receiverEmails || [])];
    newEmails[index] = value;
    updateTemplateLocally(selectedType, { receiverEmails: newEmails });
  };

  const handleAddEmail = () => {
    if (!selectedType) return;
    const newEmails = [...(currentTemplate.receiverEmails || []), ''];
    updateTemplateLocally(selectedType, { receiverEmails: newEmails });
  };

  const handleRemoveEmail = (index) => {
    if (!selectedType) return;
    const newEmails = (currentTemplate.receiverEmails || []).filter((_, i) => i !== index);
    updateTemplateLocally(selectedType, { receiverEmails: newEmails });
  };

  const handleSave = async () => {
    if (!selectedType) return;
    const success = await saveTemplate(selectedType);
    if (success) {
      messageApi.success(`Template saved successfully!`);
      setSelectedType(null);
    }
  };

  const handleReset = () => {
    if (!selectedType) return;
    resetTemplate(selectedType);
  };

  return (
    <div className="min-h-screen bg-background">
      {contextHolder}
      {/* Header */}
      <div className="emailheader" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}>
        <div className="max-w-6xl mx-auto">
          <Title level={2} style={{ color: 'white', marginBottom: 4 }}>
            Admin Email Template Configuration
          </Title>
          <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: 14 }}>
            Manage your admin email templates for booking confirmations, reschedules, and cancellations
          </Text>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-8 py-8 custom-spacing">
        <div className="grid gap-6">
          {/* Template List */}
          <Card 
            bordered
            styles={{ body: { padding: 24 } }}
          >
       
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={templates}
                rowKey="id"
                pagination={false}
                bordered={false}
              />
            )}
          </Card>

          {/* Editor Section */}
          <TemplateEditorForm
            selectedType={selectedType}
            template={currentTemplate}
            isSaving={isSaving}
            onClose={() => setSelectedType(null)}
            onSubjectChange={handleSubjectChange}
            onContentChange={handleContentChange}
            onEmailChange={handleEmailChange}
            onAddEmail={handleAddEmail}
            onRemoveEmail={handleRemoveEmail}
            onSave={handleSave}
            onReset={handleReset}
            onPreview={() => setShowPreview(true)}
          />
        </div>
      </div>

      {/* Preview Modal */}
      <TemplatePreview
        open={showPreview}
        onClose={() => setShowPreview(false)}
        subject={currentTemplate?.subject || ''}
        content={currentTemplate?.content || ''}
      />
    </div>
  );
};
